var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Modal_1;
import React, { Fragment, PureComponent } from 'react';
import { createPortal } from 'react-dom';
import { KeyCode, ModalButtonType, ModalType, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { ApplicationContext, DocumentContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, fragment, func, object, oneOf, oneOfType, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableCloseEvent, CloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ThemeService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { SubmittableFragment } from '@philips-internal/oswald-ui-engine';
import { classNames, createAbort, enumValues, generateUUID, isContentfulString, isEnumValid, isFunction, isMobileResolution, isXSmallResolution } from '@philips-internal/oswald-ui-utils';
import { GlobalActionsStackProvider } from '../../context/global-actions-stack-context.js';
import { GlobalActionsStackManager } from '../global-actions-stack-manager/global-actions-stack-manager.js';
import { ModalButtonDock } from './components/modal-button-dock.js';
import { ModalHeader } from './components/modal-header.js';
import { ModalAPI } from './modal-api.js';
import { CustomModalEvent } from './modal-constants.js';
import './modal.css';
import './modal-fullscreen.css';
import './modal-dialog.css';
import './modal-default.css';
const dockButtonShape = shape({
    action: func,
    title: string,
    type: oneOf(enumValues(ModalButtonType)),
});
const ModalContainer = {
    name: '',
};
let Modal = Modal_1 = class Modal extends PureComponent {
    constructor() {
        super(...arguments);
        this.fragmentInstanceUuid = generateUUID();
        this.fragmentUuid = generateUUID();
        this.titleUuid = generateUUID();
        this.state = {};
        this.shouldAddNavigation = () => {
            const { type } = this.props;
            return (Modal_1.hasApplicationBar()
                && Boolean(this.applicationContext)
                && type !== ModalType.FULLSCREEN
                && isMobileResolution());
        };
        this.addNavigationItem = () => {
            if (!this.applicationContext) {
                return;
            }
            this.popNavigationItem = this.applicationContext.pushNavigationItem(() => void this.closeModal());
        };
        this.discardNavigationItem = () => {
            if (isFunction(this.popNavigationItem)) {
                this.popNavigationItem();
            }
        };
        this.cancelModal = (event) => {
            const { onCancel } = this.props;
            void this.closeModal().then((result) => {
                if (!result) {
                    return;
                }
                if (isFunction(onCancel)) {
                    onCancel(event);
                }
            });
        };
        this.closeModal = () => {
            return this.shouldClose().then((aborted) => {
                if (aborted) {
                    if (this.shouldAddNavigation()) {
                        this.addNavigationItem();
                    }
                    return false;
                }
                const { onClose } = this.props;
                if (isFunction(onClose)) {
                    onClose(new CloseEvent());
                }
                return true;
            });
        };
        this.submitModal = (event) => {
            const { onSubmit } = this.props;
            void this.closeModal().then((result) => {
                if (!result) {
                    return;
                }
                if (isFunction(onSubmit)) {
                    onSubmit(event);
                }
            });
        };
        // Has to be private, otherwise typescript will complain about
        // "Public property 'onDocumentClick' of exported class has or is using private name 'MouseEvent'"
        this.onDocumentClick = (event) => {
            const { closeable, closeByDocument } = this.props;
            const { modalElement } = this.state;
            const { target } = event;
            if (!(target instanceof HTMLDivElement)) {
                return;
            }
            const { classList } = target;
            if (!(closeByDocument && closeable))
                return;
            if (!classList.contains('c-modal'))
                return;
            if (modalElement !== target)
                return;
            void this.closeModal();
        };
        this.onDocumentKeydown = (event) => {
            const { closeable, closeByEscape } = this.props;
            if (closeByEscape && closeable && event.keyCode === KeyCode.ESC) {
                void this.closeModal();
            }
        };
        this.focus = () => {
            const { focusOnOpen } = this.props;
            const { modalElement } = this.state;
            focusOnOpen && modalElement && modalElement.focus();
        };
        this.modalRef = (element) => {
            if (element) {
                this.setState({ modalElement: element }, this.focus);
            }
        };
        this.actionWrapper = (action) => {
            return () => {
                const actionResult = action();
                if (actionResult) {
                    void actionResult.then(aborted => !aborted && this.closeModal());
                }
                else {
                    void this.closeModal();
                }
            };
        };
    }
    /**
     * This method is here to give a specific class for the <Modal> component, which determines that when the component
     * is of the `DEFAULT` type, it will have a blank space at the top, which will make the <ApplicationBar> visible.
     *
     * Forgive me Father, for I have implemented a WOS.
     */
    static hasApplicationBar() {
        const element = document.querySelector('.c-application-bar');
        return Boolean(element);
    }
    componentDidMount() {
        const { document } = this.context;
        document?.documentElement.classList.add('u-prevent-scroll');
        if (this.shouldAddNavigation()) {
            this.addNavigationItem();
        }
        this.notifyApplicationBar(CustomModalEvent.MOUNT);
    }
    componentDidUpdate(prevProps) {
        const { fragment } = this.props;
        if (fragment !== prevProps.fragment) {
            this.fragmentInstanceUuid = generateUUID();
            this.fragmentUuid = generateUUID();
        }
    }
    componentWillUnmount() {
        const { document } = this.context;
        document?.documentElement.classList.remove('u-prevent-scroll');
        this.discardNavigationItem();
        this.notifyApplicationBar(CustomModalEvent.UNMOUNT);
    }
    shouldNotifyApplicationBar() {
        const { type } = this.props;
        return Modal_1.hasApplicationBar() && isXSmallResolution() && type === ModalType.DEFAULT;
    }
    notifyApplicationBar(event) {
        const { closeable } = this.props;
        if (!this.shouldNotifyApplicationBar()) {
            return;
        }
        const applicationBar = document.querySelector('.c-application-bar');
        const showNavigation = Boolean(closeable);
        const detail = { showNavigation };
        const customEvent = new CustomEvent(event, { detail });
        applicationBar?.dispatchEvent(customEvent);
    }
    getType() {
        const { type } = this.props;
        return type;
    }
    getClassName() {
        const { className, closeable, noPadding, theme } = this.props;
        const modalClasses = {
            'c-modal': true,
            'c-modal--no-padding': Boolean(noPadding),
            [`c-modal--${this.getType()}`]: isEnumValid(ModalType, this.getType()),
            'has-application-bar': Modal_1.hasApplicationBar(),
            'has-close-button': Boolean(closeable),
            'is-open': true,
            [`t-content-${theme}`]: isEnumValid(ThemeTonalRange, theme),
            [`${className}`]: Boolean(className),
        };
        return classNames(modalClasses);
    }
    getModalElement() {
        return this.state.modalElement ?? null;
    }
    getModalScrollContent() {
        const modalElement = this.getModalElement();
        if (modalElement === null) {
            return null;
        }
        return modalElement.querySelector('.c-modal__content--with-stack-manager');
    }
    shouldClose() {
        const { onBeforeClose } = this.props;
        const abort = createAbort();
        if (isFunction(onBeforeClose)) {
            onBeforeClose(new AbortableCloseEvent({ abort }));
        }
        return abort.promise;
    }
    renderButtons() {
        const { dockButtons } = this.props;
        if (!dockButtons) {
            return null;
        }
        return React.createElement(ModalButtonDock, { actionWrapper: this.actionWrapper, buttons: dockButtons });
    }
    renderContent() {
        const { children, contextData, fragment, uuid } = this.props;
        const className = classNames({
            'c-modal__content': true,
            'u-no-padding': true,
            'u-overflow-auto': true,
        });
        if (fragment) {
            const fragmentProps = {
                contextData,
                disposable: true,
                fragment,
                instanceUUID: this.fragmentInstanceUuid,
                onCancel: this.cancelModal,
                onSubmit: this.submitModal,
                uuid: this.fragmentUuid,
            };
            const fragmentComponent = (React.createElement(SubmittableFragment, { ...fragmentProps }));
            return (React.createElement("div", { className: className }, this.renderContentWithActionStackManager(fragmentComponent)));
        }
        return (React.createElement(Container, { className: className, uuid: uuid }, this.renderContentWithActionStackManager(children)));
    }
    renderContentWithActionStackManager(children) {
        const { dialogDescription } = this.props;
        const className = classNames({
            'c-modal__content--with-stack-manager': true,
            'u-fill-available-space': true,
            'u-overflow-auto': true,
            'u-scrollbar': !isXSmallResolution(),
        });
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: className }, dialogDescription
                ? this.renderDialogDescription()
                : children),
            React.createElement(GlobalActionsStackManager, { hideOnDesktop: false })));
    }
    renderDialogDescription() {
        const { dialogDescription } = this.props;
        return (React.createElement("div", { className: "u-overflow-auto c-modal__description" }, MessageService.translate(dialogDescription ?? '')));
    }
    renderModal() {
        const { closeable, hideHeaderOnMobile, title, type, uuid } = this.props;
        const { modalElement } = this.state;
        const modalContext = {
            ...this.context,
            scrollElement: modalElement,
        };
        const isDefault = type === ModalType.DEFAULT;
        const isDialog = type === ModalType.DIALOG;
        const isCloseable = Boolean(closeable);
        const shouldShowClose = isCloseable && ((isDialog && isXSmallResolution()) || (!isXSmallResolution() || !isDefault));
        const headerHaveContent = (shouldShowClose || Boolean(title)) && !(hideHeaderOnMobile && isXSmallResolution());
        const titleIdentifier = `c-modal__title-${isContentfulString(uuid) ? uuid : this.titleUuid}`;
        return (React.createElement(DocumentContext.Provider, { value: modalContext },
            React.createElement(GlobalActionsStackProvider, null,
                React.createElement("div", { "aria-label": !headerHaveContent && title ? MessageService.translate(title) : undefined, "aria-labelledby": headerHaveContent && title ? titleIdentifier : undefined, className: this.getClassName(), onClick: this.onDocumentClick, onKeyDownCapture: this.onDocumentKeydown, ref: this.modalRef, role: "dialog", tabIndex: 0 },
                    React.createElement("div", { className: "c-modal__wrapper" },
                        headerHaveContent
                            ? React.createElement(ModalHeader, { id: titleIdentifier, onClose: () => {
                                    void this.closeModal();
                                }, showClose: shouldShowClose, title: title })
                            : null,
                        this.renderButtons(),
                        this.renderContent())))));
    }
    render() {
        const { document } = this.context;
        const { container = document.body } = this.props;
        if (!document && !container) {
            return null;
        }
        return (React.createElement(ApplicationContext.Context.Consumer, null, context => {
            this.applicationContext = context;
            return (createPortal(this.renderModal(), container));
        }));
    }
};
Modal.contextType = DocumentContext;
Modal.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    closeable: bool,
    closeByDocument: bool,
    closeByEscape: bool,
    container: object.notEnumerable(),
    contextData: object.notEnumerable(),
    dialogDescription: string.notEnumerable(),
    dockButtons: arrayOf(dockButtonShape).notEnumerable(),
    focusOnOpen: bool,
    fragment: fragment.notEnumerable(),
    hideHeaderOnMobile: bool,
    noPadding: bool,
    onBeforeClose: eventListener,
    onCancel: eventListener,
    onClose: eventListener,
    onSubmit: eventListener,
    theme: oneOf(['', ...enumValues(ThemeTonalRange)]),
    title: expression,
    type: oneOf(enumValues(ModalType)),
    uuid: string,
};
Modal.defaultProps = {
    closeable: true,
    closeByDocument: true,
    closeByEscape: true,
    focusOnOpen: true,
    noPadding: false,
    theme: ThemeService.getTheme().contentTonalRange,
    type: ModalType.DEFAULT,
    uuid: '',
};
Modal = Modal_1 = __decorate([
    AwesomeComponent({ api: ModalAPI, containers: [ModalContainer], type: ComponentType.LAYOUT })
], Modal);
export { Modal, };
//# sourceMappingURL=modal.js.map